/**
 * Google Identity Services (GIS) integration for the dashboard.
 *
 * Loads the `gsi/client` script on demand, exposes a `signIn()` helper
 * that returns the ID token + decoded claims, and caches the active
 * session in sessionStorage so a tab refresh doesn't bounce the user
 * back to the login screen.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SESSION_KEY = 'fccers.auth.session';

export interface AuthSession {
  idToken: string;
  email: string;
  name?: string;
  picture?: string;
  /** Unix-ms expiry from the JWT `exp` claim. */
  expiresAt: number;
}

/** The slice of the GIS `google.accounts.id` global this module touches. */
interface GisAccountsId {
  initialize(config: Record<string, unknown>): void;
  renderButton(container: HTMLElement, options: Record<string, unknown>): void;
  disableAutoSelect?(): void;
}

function gisAccountsId(): GisAccountsId | undefined {
  return (window as unknown as { google?: { accounts?: { id?: GisAccountsId } } })
    .google?.accounts?.id;
}

let gisLoaded: Promise<void> | null = null;

function loadGis(): Promise<void> {
  if (gisLoaded) return gisLoaded;
  gisLoaded = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${GIS_SRC}"]`)) {
      // Script already added — wait for window.google to appear.
      const wait = () => {
        if (gisAccountsId()) resolve();
        else setTimeout(wait, 50);
      };
      wait();
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return gisLoaded;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split('.')[1];
  if (!part) throw new Error('Invalid JWT');
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '==='.slice((b64.length + 3) % 4);
  return JSON.parse(atob(padded));
}

export function getSession(): AuthSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as AuthSession;
    if (Date.now() >= s.expiresAt) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  // Disable Google's auto-select / one-tap so the next visit is a fresh prompt.
  gisAccountsId()?.disableAutoSelect?.();
}

/**
 * Trigger Google sign-in via the GIS button. The caller renders a div with
 * id="google-signin-btn"; this function tells GIS to render its branded
 * button into that container. Returns a promise that resolves when the
 * user picks an account and the credential callback fires.
 */
export function initGoogleSignIn(
  clientId: string,
  containerId: string,
  onSession: (session: AuthSession) => void,
): Promise<void> {
  return loadGis().then(() => {
    const accountsId = gisAccountsId();
    if (!accountsId) {
      throw new Error('Google Identity Services not available');
    }
    accountsId.initialize({
      client_id: clientId,
      callback: (resp: { credential?: string }) => {
        if (!resp.credential) return;
        try {
          const payload = decodeJwtPayload(resp.credential) as {
            email?: string;
            name?: string;
            picture?: string;
            exp?: number;
          };
          if (!payload.email || !payload.exp) return;
          const session: AuthSession = {
            idToken: resp.credential,
            email: payload.email,
            name: payload.name,
            picture: payload.picture,
            expiresAt: payload.exp * 1000,
          };
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
          onSession(session);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to decode Google credential', err);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: false,
    });
    const container = document.getElementById(containerId);
    if (container) {
      accountsId.renderButton(container, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
        logo_alignment: 'left',
        width: 300,
      });
    }
  });
}
